import React from 'react';
import styled from 'styled-components';
import LocaleLink from './LocaleLink';
import { motion } from 'framer-motion';
import { useContent } from '../hooks/useContent';
import { useNewsletter, StatusMessage } from './shared/SharedComponents';

const FooterContainer = styled.footer`
  border-top: 1px solid #e6e6e6;
  padding: 64px 0 32px;
  background: white;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 64px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 0 16px;
  }
`;

const CompanyInfo = styled.div`
  h3 {
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 24px;
    color: #1d334e;
  }
  
  p {
    color: rgba(29, 51, 78, 0.8);
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 1.5;
  }
  
  address {
    font-style: normal;
    color: rgba(29, 51, 78, 0.8);
    font-size: 14px;
    line-height: 1.5;
  }
  
  address p {
    margin-bottom: 12px;
  }
  
  a {
    color: #1d334e;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      color: #BF1922;
    }
  }
`;

const NewsletterWrapper = styled.div`
  margin-top: 20px;
  max-width: 350px;
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: flex-start;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid rgba(29, 51, 78, 0.2);
  border-radius: 8px;
  font-size: 14px;
  color: #1d334e;
  background: white;
  transition: border-color 0.3s ease;
  min-width: 0;
  
  &:focus {
    outline: none;
    border-color: #BF1922;
  }
  
  &::placeholder {
    color: rgba(29, 51, 78, 0.5);
  }
`;

const NewsletterButton = styled.button`
  padding: 10px 18px;
  background: linear-gradient(135deg, #1d334e, #BF1922);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(29, 51, 78, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const NewsletterStatusMessage = styled(StatusMessage)`
  margin-top: 8px;
  font-size: 13px;
  padding: 8px 12px;
`;

const FooterLogo = styled(motion.img)`
  height: 50px;
  width: auto;
  margin-bottom: 24px;
  display: block;
  
  @media (max-width: 768px) {
    height: 45px;
  }
`;

const BusinessHours = styled.div`
  h4 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 24px;
    color: #1d334e;
  }
`;

const HoursGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const DayHours = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e6e6e6;
  
  &:last-child {
    border-bottom: none;
  }
  
  .day {
    color: #1d334e;
    font-weight: 500;
    font-size: 14px;
    min-width: 60px;
  }
  
  time {
    color: rgba(29, 51, 78, 0.8);
    font-size: 14px;
    text-align: right;
  }
`;

const BottomBar = styled.div`
  border-top: 1px solid #e6e6e6;
  margin-top: 48px;
  padding-top: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: rgba(29, 51, 78, 0.8);
  font-size: 14px;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const FooterNavWrapper = styled.div`
  @media (max-width: 768px) {
    order: 1;
  }
`;

const FooterNav = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
`;

const FooterLink = styled(LocaleLink)`
  color: rgba(29, 51, 78, 0.8);
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
  transition: color 0.3s ease;
  
  &:hover {
    color: #BF1922;
  }
`;

const Footer = () => {
  const { contactInfo, footerInfo, home } = useContent();
  const { email, setEmail, isSubmitting, status, handleSubmit } = useNewsletter();

  const currentYear = new Date().getFullYear();
  const businessHours = footerInfo.businessHours || [];

  // Helper function to convert day and hours to datetime value
  const getDateTimeValue = (dayKey, day, hours) => {
    if (hours === 'Closed' || hours === '休息') return '';
    const dayMap = { 'Mon': 'Mo', 'Tue': 'Tu', 'Wed': 'We', 'Thu': 'Th', 'Fri': 'Fr', 'Sat': 'Sa', 'Sun': 'Su' };
    const dayAbbr = dayMap[dayKey || day] || day;
    const [start, end] = hours.split(/[ – -]/);
    if (start && end && /\d/.test(start) && /\d/.test(end)) {
      const startTime = start.replace(/\s*(am|pm|上午|下午)/gi, '').trim().replace(':', '');
      const endTime = end.replace(/\s*(am|pm|上午|下午)/gi, '').trim().replace(':', '');
      if (startTime && endTime) return `${dayAbbr} ${startTime}-${endTime}`;
    }
    return '';
  };

  return (
    <FooterContainer>
      <FooterContent>
        <CompanyInfo>
          <FooterLogo
            src="/logo/logo@1x.png"
            srcSet="/logo/logo@1x.png 1x, /logo/logo@2x.png 2x"
            alt={footerInfo.companyName || 'APEXGATE GLOBAL'}
            width="405"
            height="80"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
          />
          <motion.address
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
          >
            {contactInfo.phones?.sea && (
              <p>
                <a href={`tel:${contactInfo.phones.sea.replace(/[^+\d]/g, '')}`}>
                  {contactInfo.phones.sea}
                </a>
              </p>
            )}
            {contactInfo.email && (
              <p>
                <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
              </p>
            )}
            {contactInfo.website && (
              <p>
                <a
                  href={`https://${contactInfo.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {contactInfo.website}
                </a>
              </p>
            )}
            <p>
              {footerInfo.companyLegalName}{footerInfo.brn ? ` | BRN: ${footerInfo.brn}` : ''}
            </p>
            {footerInfo.address && (
              <p>
                {footerInfo.registeredOfficeLabel || 'Registered office:'}{' '}
                {footerInfo.address}
              </p>
            )}
          </motion.address>
          <NewsletterWrapper>
            <NewsletterForm onSubmit={handleSubmit}>
              <NewsletterInput
                type="email"
                placeholder={footerInfo.newsletterPlaceholder || home?.newsletter?.placeholder || "Enter your email"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
              />
              <NewsletterButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Subscribing...' : footerInfo.newsletterButton || home?.newsletter?.buttonText || 'Subscribe'}
              </NewsletterButton>
            </NewsletterForm>
            {status && (
              <NewsletterStatusMessage className={status === 'success' ? 'success' : 'error'}>
                {status === 'success'
                  ? 'Thank you for subscribing!'
                  : status
                }
              </NewsletterStatusMessage>
            )}
          </NewsletterWrapper>
        </CompanyInfo>

        <BusinessHours>
          <motion.h4
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true }}
          >
            {footerInfo.businessHoursLabel || 'Business Hours'}
          </motion.h4>
          <HoursGrid>
            {businessHours.map((item, index) => (
              <DayHours
                key={item.day}
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
                viewport={{ once: true }}
              >
                <span className="day">{item.day}</span>
                {item.hours !== 'Closed' && item.hours !== '休息' ? (
                  <time dateTime={getDateTimeValue(item.dayKey, item.day, item.hours)}>
                    {item.hours}
                  </time>
                ) : (
                  <time>{item.hours}</time>
                )}
              </DayHours>
            ))}
          </HoursGrid>
        </BusinessHours>
      </FooterContent>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <BottomBar>
          <FooterNavWrapper>
            <FooterNav>
              {Array.isArray(footerInfo.links) && footerInfo.links.length > 0 ? (
                footerInfo.links.map((link) => (
                  <FooterLink key={link.path} to={link.path}>{link.label}</FooterLink>
                ))
              ) : (
                <>
                  <FooterLink to="/about">About</FooterLink>
                  <FooterLink to="/contact">Contact</FooterLink>
                  <FooterLink to="/disclaimer">Disclaimer</FooterLink>
                  <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
                  <FooterLink to="/terms-and-conditions">Terms &amp; Conditions</FooterLink>
                  <FooterLink to="/legal-notice">Legal Notice</FooterLink>
                </>
              )}
            </FooterNav>
          </FooterNavWrapper>
          <Copyright>© {currentYear} {footerInfo.copyrightCompanyName} {footerInfo.copyrightSuffix || 'All rights reserved.'}</Copyright>
        </BottomBar>
      </div>
    </FooterContainer>
  );
};

export default Footer;